import {
  Box,
  Container,
  VStack,
  Text,
  Divider,
  useColorMode,
} from "@chakra-ui/react";

const BdaySpeechPage = () => {
  const { colorMode } = useColorMode();

  const bg = colorMode === "light" ? "#1C1C1C" : "#0a0a0a";
  const textColor = "#FAF9F6";
  const dimColor = "#999";
  const gold = "#D4AF37";

  return (
    <Box bg={bg} minH="100vh" py={{ base: 8, md: 16 }} px={4}>
      <Container maxW="container.sm">
        <VStack spacing={10} align="stretch">

          {/* Title */}
          <VStack spacing={1}>
            <Text fontSize="xs" color={dimColor} textTransform="uppercase" letterSpacing="0.3em">
              Tom's 40th — Speech Notes
            </Text>
            <Divider borderColor={gold} opacity={0.3} />
          </VStack>

          {/* ═══════════════════════════════════════════ */}
          {/* LONG-FORM VERSION — read through, feel it  */}
          {/* ═══════════════════════════════════════════ */}

          <VStack spacing={1} align="center">
            <Text fontSize="sm" color={gold} textTransform="uppercase" letterSpacing="0.25em" fontWeight="600">
              Full Version
            </Text>
            <Text fontSize="xs" color={dimColor} fontStyle="italic">
              Read through to feel the rhythm — don't memorise it
            </Text>
          </VStack>

          <Box
            bg="rgba(255,255,255,0.03)"
            borderRadius="md"
            p={{ base: 5, md: 8 }}
            borderLeft="3px solid"
            borderColor={gold}
          >
            <VStack spacing={6} align="start">
              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                Thanks everyone for coming today. I'm not gonna do a long speech because honestly… I don't know a lot.
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                I don't know much about history. I don't even know much about my own history. I've actually started to question — I don't even know if this hand is real… or if we're all just living in a simulation. Don't worry — I'm not losing my grip on reality. I'm just trying to take a broader view and understand what there is to understand. I don't know how the world got started. I don't know why we're here. I don't even know why YOU'RE here — but I'm grateful for our minds to be meeting on another day of existence.
              </Text>

              <Text color={dimColor} fontSize="xs" fontStyle="italic">
                ▸ pause for laughs
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                I don't know if March 14th is even a real date or if someone just made it up. I don't even really know who I am half the time.
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                But I do know <strong>one</strong> thing.
              </Text>

              <Text color={dimColor} fontSize="xs" fontStyle="italic">
                ▸ let it land
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                I know that 40 years ago, on this day, I was born…
              </Text>

              <Text color={gold} fontSize={{ base: "lg", md: "xl" }} lineHeight="1.9" fontWeight="600">
                Right THERE.
              </Text>

              <Text color={dimColor} fontSize="xs" fontStyle="italic">
                ▸ point at the Mater Hospital — one block away
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                At the Mater Hospital. Literally one block from where we're standing right now.
              </Text>

              <Text color={dimColor} fontSize="xs" fontStyle="italic">
                ▸ let people look / react
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                And the only reason I even know that… is because my parents told me. I don't remember it myself. I was having a pretty rough day apparently.
              </Text>

              <Text color={dimColor} fontSize="xs" fontStyle="italic">
                ▸ laughs
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                So to Mum and Dad — thank you. For that day, and for every day since. Everything I am started with you two.
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                To my family — you know who you are. Thank you for putting up with me for 40 years. That's commitment.
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                To my mates — some of you have been around since I was a kid. Some of you are newer. All of you are here today and that means everything.
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                Look — I don't know much. I've made that pretty clear. But what I do know is that right now, all of us are here. Together. In this moment. And that's actually all any of us really know for sure.
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                So I invite you to celebrate that. Not just my 40 years — celebrate this moment. This brief little window of time that none of us really know anything about either.
              </Text>

              <Text color={textColor} fontSize={{ base: "md", md: "lg" }} lineHeight="1.9" fontWeight="300">
                I'm just really grateful you're all spending some of yours with me.
              </Text>

              <Text color={dimColor} fontSize="xs" fontStyle="italic">
                ▸ raise glass
              </Text>

              <Text color={gold} fontSize={{ base: "lg", md: "xl" }} lineHeight="1.9" fontWeight="600">
                Cheers.
              </Text>
            </VStack>
          </Box>

          {/* ═══════════════════════════════════════════ */}
          {/* BULLET POINT VERSION — glance-at reference */}
          {/* ═══════════════════════════════════════════ */}

          <Box py={12}>
            <Divider borderColor={gold} opacity={0.4} />
          </Box>

          <VStack spacing={1} align="center">
            <Text fontSize="sm" color={gold} textTransform="uppercase" letterSpacing="0.25em" fontWeight="600">
              Bullet Points
            </Text>
            <Text fontSize="xs" color={dimColor} fontStyle="italic">
              Quick reference — just hit these beats
            </Text>
          </VStack>

          {/* THE OPENING — grab attention */}
          <VStack spacing={4} align="start">
            <Text fontSize="xs" color={gold} textTransform="uppercase" letterSpacing="0.2em">
              The Build-Up — "I Don't Know"
            </Text>
            <BulletPoint>
              Thanks everyone for coming. Not gonna do a <Key>long speech</Key> because honestly...
            </BulletPoint>
            <BulletPoint bold>
              I <Key>don't know</Key> a lot.
            </BulletPoint>
            <BulletPoint>
              Don't know much about <Key>history</Key>.
            </BulletPoint>
            <BulletPoint>
              Don't even know much about <Key>my own history</Key>.
            </BulletPoint>
            <BulletPoint>
              Started to question — don't even know if this <Key>hand is real</Key>… or if we're all just in a <Key>simulation</Key>.
            </BulletPoint>
            <BulletPoint>
              Don't worry — not <Key>losing my grip</Key> on reality. Just trying to take a broader view and understand what there is to understand.
            </BulletPoint>
            <BulletPoint>
              Don't know how the <Key>world got started</Key>.
            </BulletPoint>
            <BulletPoint>
              Don't know <Key>why we're here</Key>.
            </BulletPoint>
            <BulletPoint>
              Don't even know why <Key>YOU'RE</Key> here — but grateful for our minds to be meeting on <Key>another day of existence</Key>.
            </BulletPoint>
            <Pause label="pause for laughs" />
            <BulletPoint>
              Don't know if <Key>March 14th</Key> is even a real date or if someone just made it up.
            </BulletPoint>
            <BulletPoint>
              Don't even really know <Key>who I am</Key> half the time.
            </BulletPoint>
          </VStack>

          <Divider borderColor={gold} opacity={0.2} />

          {/* THE TURN */}
          <VStack spacing={4} align="start">
            <Text fontSize="xs" color={gold} textTransform="uppercase" letterSpacing="0.2em">
              The Turn — "But I Do Know One Thing"
            </Text>
            <BulletPoint bold>
              But I do know <Key>ONE thing</Key>.
            </BulletPoint>
            <Pause label="let it land" />
            <BulletPoint bold>
              <Key>40 years ago</Key>, on this day, I was born...
            </BulletPoint>
            <BulletPoint bold accent>
              Right <Key>THERE</Key>.
            </BulletPoint>
            <Pause label="point at the Mater Hospital — one block away" />
            <BulletPoint>
              <Key>Mater Hospital</Key>. Literally one block from where we're standing.
            </BulletPoint>
            <Pause label="let people look / react" />
            <BulletPoint>
              Only reason I know that... is because my <Key>parents told me</Key>.
            </BulletPoint>
            <Pause label="laughs" />
            <BulletPoint>
              Don't remember it myself. Having a pretty <Key>rough day</Key> apparently.
            </BulletPoint>
          </VStack>

          <Divider borderColor={gold} opacity={0.2} />

          {/* THE THANKS */}
          <VStack spacing={4} align="start">
            <Text fontSize="xs" color={gold} textTransform="uppercase" letterSpacing="0.2em">
              The Thanks — Keep It Short
            </Text>
            <BulletPoint>
              <Key>Mum and Dad</Key> — thank you. For that day, and every day since. Everything I am started with you two.
            </BulletPoint>
            <BulletPoint>
              <Key>Family</Key> — thank you for putting up with me for 40 years. That's commitment.
            </BulletPoint>
            <BulletPoint>
              <Key>Mates</Key> — some since I was a kid, some newer. All here today and that means <Key>everything</Key>.
            </BulletPoint>
          </VStack>

          <Divider borderColor={gold} opacity={0.2} />

          {/* THE CLOSE */}
          <VStack spacing={4} align="start">
            <Text fontSize="xs" color={gold} textTransform="uppercase" letterSpacing="0.2em">
              Bring It Home
            </Text>
            <BulletPoint>
              Look — <Key>don't know much</Key>. Made that pretty clear.
            </BulletPoint>
            <BulletPoint bold>
              But right now, all of us are <Key>here. Together. In this moment</Key>.
            </BulletPoint>
            <BulletPoint>
              That's actually all any of us really <Key>know for sure</Key>.
            </BulletPoint>
            <BulletPoint>
              Celebrate <Key>this moment</Key>. This brief little window of time that none of us really know anything about either.
            </BulletPoint>
            <BulletPoint bold>
              Just really <Key>grateful</Key> you're all spending some of yours with me.
            </BulletPoint>
            <Pause label="raise glass" />
            <BulletPoint bold accent>
              <Key>Cheers.</Key>
            </BulletPoint>
          </VStack>

          {/* Footer reminder */}
          <Divider borderColor={gold} opacity={0.2} />
          <Text fontSize="xs" color={dimColor} textAlign="center" fontStyle="italic">
            ~2 minutes · Keep it loose · Don't read it word for word · Just hit the bullet points
          </Text>

        </VStack>
      </Container>
    </Box>
  );
};

// Bullet point component
const BulletPoint = ({ children, bold, accent }: { children: React.ReactNode; bold?: boolean; accent?: boolean }) => (
  <Text
    fontSize={{ base: "xl", md: "2xl" }}
    color={accent ? "#D4AF37" : "#FAF9F6"}
    fontWeight={bold ? "700" : "400"}
    lineHeight="1.7"
    pl={6}
    position="relative"
    _before={{
      content: '"•"',
      position: "absolute",
      left: 0,
      color: accent ? "#D4AF37" : "rgba(255,255,255,0.4)",
      fontWeight: "700",
    }}
  >
    {children}
  </Text>
);

// Key word/phrase highlight — gold underline
const Key = ({ children }: { children: React.ReactNode }) => (
  <Text
    as="span"
    color="#D4AF37"
    textDecoration="underline"
    textDecorationColor="#D4AF37"
    textUnderlineOffset="3px"
    fontWeight="600"
  >
    {children}
  </Text>
);

// Stage direction / pause marker
const Pause = ({ label }: { label: string }) => (
  <Text
    fontSize="xs"
    color="#D4AF37"
    fontStyle="italic"
    pl={4}
    opacity={0.7}
  >
    ▸ {label}
  </Text>
);

export default BdaySpeechPage;
